import { ReactNode } from 'react';
import { AppearanceType } from '../designConstants';
import { Header } from '../StickyTable';

/** @deprecated Header height for tables that still pass a fixed `rowHeight`; prefer `density`. */
export const ROW_HEIGHT = 25;
export const PRETTY_DATE_FORMAT = 'yyyy-MM-dd hh:mma';

export interface SelectorOption {
  label: string | number;
  value: string | number;
}

export type SelectorOptions = Record<string, SelectorOption[] | Record<string, SelectorOption[]>>;

export type TableRow<T extends object> = T & {
  rowKey: string;
};

export interface SaveChangesInput<T> {
  addedData: T[];
  modifiedData: T[];
  deletedData: T[];
  // Clears pending added/modified/deleted state. Shows the built-in "Changes Saved" dialog by
  // default; pass { showSuccessDialog: false } when the caller only wants the pending state
  // cleared (e.g. discarding a batch that had nothing to actually persist) without claiming a
  // save happened.
  cleanup: (options?: { showSuccessDialog?: boolean }) => void;
}

export interface ModalEditWidgetHandle<T> {
  trackModify: (rowKey: string, row: T) => void;
  trackAdd: (row: T) => string;
  trackDelete: (rowKey: string) => void;
  /**
   * Opens the built-in row editor in "adding" mode -- the same thing the header Add button
   * triggers. Lets a caller supply its own Add affordance (e.g. inside emptyContent, which hides
   * the header Add button once the table has zero rows) that still opens the real add flow.
   */
  openAddModal: () => void;
}

export interface FileToUpload {
  fileName: string;
  file: File;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

export interface CustomModalEditWidgetHeader extends Header {
  customRenderer?: (data: any, onChange: (newData: any) => void) => ReactNode;
  dynamicOptions?: (row: any) => string;
  isClearable?: boolean;
  /** Cleared numeric inputs use undefined, so JSON omits the field instead of sending null. */
  omitEmptyNumber?: boolean;
  isValidNewOption?: (inputValue: string) => boolean;
  rendererType?: (data: any) => string;
  readOnly?: boolean;
  /** Whether the select dropdown uses React Select (searchable). Defaults to false. */
  searchable?: boolean;
  /** Whether new options can be typed and created. Only applies when searchable is true. Defaults to false. */
  creatable?: boolean;
  /** When true, this column is omitted from the pending changes panel. */
  hideInPendingChanges?: boolean;
  /** Visible textarea rows for 'query' and 'richText' field types. Defaults to 4. */
  rows?: number;
  /** Marks the field's modal label with a required asterisk. Display only -- enforce it in checkIsValid. */
  required?: boolean;
  /** Label shown in the row editor instead of `label` (which stays the table column header). */
  modalLabel?: string;
  /** Hint line rendered above this field's modal row (set it on any one field in the row). */
  modalNote?: string;
  /** Leaves the field out of the row editor -- always, or for rows where the predicate is true. */
  hideInModal?: boolean | ((data: any) => boolean);
}

export interface CustomFooterButton<T> {
  label: string | null | undefined;
  onClick: (rowData: T) => void;
  variant?: AppearanceType;
  showWhen?: 'always' | 'adding' | 'editing';
}

export type CheckIsValidFn<T> =
  | ((
      data: Partial<T>,
      handleInvalidMessage?: (message: string) => void
    ) => boolean | Promise<boolean>)
  | null;

// Separate from CheckIsValidFn: gates whether a row can be deleted at all, independent of
// whether its (possibly in-progress, unsaved) field values currently pass validation -- e.g. a
// row that's still an unconfirmed insert. Evaluated against the row as already committed, not
// the edited draft.
export type CheckCanDeleteFn<T> = (rowData: Partial<T>) => boolean | Promise<boolean>;
